import React, { useEffect, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { makeStyles, Paper, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Button, Typography, Modal, Box, RadioGroup, FormControlLabel, Radio, FormControl, IconButton, TextField, TableSortLabel } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { listGuidances } from '../graphql/queries';
import { deleteGuidance, updateGuidance } from '../graphql/mutations';
import { useNavigate } from 'react-router-dom';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '15px',
    backgroundColor: '#f9f9f9'
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem'
  },
  actionButton: {
    color: '#01b5e2',
    borderColor: '#01b5e2',
    '&:hover': {
      backgroundColor: '#01b5e2',
      color: 'white',
    }
  },
  linkButton: {
    color: '#007bff',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  instructionText: {
    margin: theme.spacing(2),
    textAlign: 'center',
  },
  modal: {
    position: 'absolute',
    width: '40%',
    minHeight: '40%',
    backgroundColor: theme.palette.background.paper,
    border: `4px solid #00b5e2`,
    borderRadius: '15px',
    padding: theme.spacing(4),
    top: `50%`,
    left: `50%`,
    transform: `translate(-50%, -50%)`,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    color: '#00b5e2',
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  modalSubtitle: {
    color: 'grey',
    fontSize: '1rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  radioGroup: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  closeButton: {
    color: '#00b5e2',
  },
  changeStatusButton: {
    color: '#fff',
    backgroundColor: '#00b5e2',
    '&:hover': {
      backgroundColor: '#0094c6',
    },
    borderColor: '#00b5e2',
  },
  dateTimePicker: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterInput: {
    marginBottom: theme.spacing(2),
    width: '100%',
  }
}));

const ManageGuidance = () => {
  const classes = useStyles();
  const [guidances, setGuidances] = useState([]);
  const [filteredGuidances, setFilteredGuidances] = useState([]);
  const [selectedGuidances, setSelectedGuidances] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentGuidanceId, setCurrentGuidanceId] = useState('');
  const [scheduleDateTime, setScheduleDateTime] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchGuidances();
  }, []);
  
  useEffect(() => {
    applyFilters(); // Filters only when the filterText changes
  }, [filterText]);
  
  const fetchGuidances = async () => {
    let allGuidances = [];
    let nextToken = null;
  
    do {
      try {
        const response = await API.graphql(graphqlOperation(listGuidances, {
          filter: {
            deleted: {
              eq: false
            }
          },
          nextToken: nextToken
        }));
  
        const guidanceList = response.data?.listGuidances?.items || [];
        allGuidances = [...allGuidances, ...guidanceList];
        nextToken = response.data?.listGuidances?.nextToken;
      } catch (error) {
        console.log(error);
        break; // exit if there's an error
      }
    } while (nextToken);
  
    setGuidances(allGuidances);
    // Apply the default sorting right after fetching
    handleSort('createdAt', 'desc', allGuidances); 
  };
  
  const handleSort = (column, directionOverride = null, guidancesList = filteredGuidances) => {
    const direction = directionOverride || (sortConfig.key === column && sortConfig.direction === 'asc' ? 'desc' : 'asc');
    const sortedGuidances = [...guidancesList].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredGuidances(sortedGuidances);
    setSortConfig({ key: column, direction });
  };
  
  const applyFilters = () => {
    let updatedGuidances = [...guidances];
    if (filterText) {
      updatedGuidances = updatedGuidances.filter(guidance =>
        guidance.title?.toLowerCase().includes(filterText.toLowerCase())
      );
    }
    handleSort(sortConfig.key || 'createdAt', sortConfig.direction, updatedGuidances); // Ensure the filter works with the current sort
  };


  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  const handleSelectGuidance = (guidanceId) => {
    if (selectedGuidances.includes(guidanceId)) {
      setSelectedGuidances(selectedGuidances.filter((id) => id !== guidanceId));
    } else {
      setSelectedGuidances([...selectedGuidances, guidanceId]);
    }
  };

  const handleDeleteSelectedGuidances = async () => {
    try {
      for (const guidanceId of selectedGuidances) {
        await API.graphql(graphqlOperation(deleteGuidance, {
          input: { id: guidanceId }
        }));
      }
      setSelectedGuidances([]);
      fetchGuidances();
    } catch (error) {
      console.log("error deleting");
      console.log(error);
    }
  };

  const handleChangeStatus = (guidanceId, status) => {
    setCurrentGuidanceId(guidanceId);
    setCurrentStatus(status || 'DRAFT');
    setOpenModal(true);
  };

  const handleStatusChange = async () => {
    try {
      const input = { id: currentGuidanceId, status: currentStatus };

      if (currentStatus === 'SCHEDULED' && scheduleDateTime) {
        input.scheduleDateTime = scheduleDateTime.toISOString();
      }

      await API.graphql(graphqlOperation(updateGuidance, { input }));
      setOpenModal(false);
      fetchGuidances();
    } catch (error) {
      console.error("Error updating status", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleGuidanceClick = (guidanceId) => {
    navigate(`/ManageGuidanceArticle/${guidanceId}`);
  };

  const handleAddPagesClick = (guidanceId) => {
    navigate(`/AddGuidanceContentForm/${guidanceId}`);
  };

  const handleEditGuidanceClick = (guidanceId) => {
    navigate(`/add-guidance/${guidanceId}`);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <>
        <Modal open={openModal} onClose={handleCloseModal}>
          <Box className={classes.modal}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6" className={classes.modalTitle}>Change Status</Typography>
              <IconButton className={classes.closeButton} onClick={handleCloseModal}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="subtitle1" className={classes.modalSubtitle}>
              Changing the status will have an instant impact on content visible to the users.
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup className={classes.radioGroup} value={currentStatus} onChange={(e) => setCurrentStatus(e.target.value)}>
                <FormControlLabel value="DRAFT" control={<Radio />} label="Draft" />
                <FormControlLabel value="PUBLISHED" control={<Radio />} label="Published" />
                <FormControlLabel value="TESTING" control={<Radio />} label="Testing" />
                <FormControlLabel value="SCHEDULED" control={<Radio />} label="Scheduled" />
              </RadioGroup>
              {currentStatus === 'SCHEDULED' && (
                <DateTimePicker
                  label="Select Date and Time"
                  value={scheduleDateTime}
                  onChange={setScheduleDateTime}
                  className={classes.dateTimePicker}
                  format="yyyy-MM-dd HH:mm:ss"
                />
              )}
              <Button variant="contained" className={classes.changeStatusButton} onClick={handleStatusChange}>
                Confirm
              </Button>
            </FormControl>
          </Box>
        </Modal>
        <Typography variant="subtitle1" className={classes.instructionText}>
          Click on any row in the table to view and edit guidance pages
        </Typography>
        <TextField
          className={classes.filterInput}
          label="Filter by Title"
          variant="outlined"
          value={filterText}
          onChange={handleFilterChange}
        />
        <Paper className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell></TableCell>
                <TableCell sortDirection={sortConfig.key === 'createdAt' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.key === 'createdAt'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created At
                  </TableSortLabel>
                </TableCell>
                <TableCell>Add Pages</TableCell>
                <TableCell>Edit</TableCell>
                <TableCell sortDirection={sortConfig.key === 'title' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig.key === 'title'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Change Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGuidances.map((guidance) => (
                <TableRow key={guidance.id} style={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Checkbox
                      checked={selectedGuidances.includes(guidance.id)}
                      onChange={() => handleSelectGuidance(guidance.id)}
                    />
                  </TableCell>
                  <TableCell>{guidance.createdAt ? new Date(guidance.createdAt).toLocaleString() : 'N/A'}</TableCell>
                  <TableCell>
                    <span className={classes.linkButton} onClick={() => handleAddPagesClick(guidance.id)}>
                      Add Pages
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={classes.linkButton} onClick={() => handleEditGuidanceClick(guidance.id)}>
                      Edit
                    </span>
                  </TableCell>
                  <TableCell onClick={() => handleGuidanceClick(guidance.id)}>{guidance.title}</TableCell>
                  <TableCell>{guidance.status || 'Not Set'}</TableCell>
                  <TableCell>{guidance.scheduleDateTime ? new Date(guidance.scheduleDateTime).toLocaleString() : 'Not Scheduled'}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      className={classes.changeStatusButton}
                      onClick={() => handleChangeStatus(guidance.id, guidance.status)}
                    >
                      Change Status
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDeleteSelectedGuidances}
          disabled={selectedGuidances.length === 0}
        >
          Delete Selected
        </Button>
      </>
    </MuiPickersUtilsProvider>
  );
};

export default ManageGuidance;
